import React, { useState, useMemo } from 'react';
import { translate } from '../../../Services/translation';
import {
  ExtapiInvoiceReturnRequest,
  RmaInvoice,
} from '../../../Types/MyPages/InvoicesModel';
import { sendReturnInvoicesRequest } from '../../../Actions/Invoices.action';
import RadioButton from '../../Inputs/Radiobutton';
import Icon from '../../Icon';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import MyPagesRmaSummary from './MyPagesRmaSummary';
import MyPagesRmaList from './MyPagesRmaList';

enum RmaState {
  GENERAL,
  INVOICE,
  SUMMARY,
  FINISH,
}

export default function MyPagesRma() {
  const [invoicesToReturn, setInvoicesToReturn] = useState<RmaInvoice[]>(
    []
  );
  const [rmaState, setRmaState] = useState<number>(RmaState.GENERAL);

  const onInvoiceSaved = (invoice: RmaInvoice) => {
    const index = invoicesToReturn.findIndex(
      (x) => x.documentNo == invoice.documentNo
    );

    if (index === -1) {
      // New invoice rows
      setInvoicesToReturn((invoicesToReturn) => [
        ...invoicesToReturn,
        invoice,
      ]);
    } else if (invoice.rows.length == 0) {
      // Unselected all rows, remove from array
      const invoicesToReturnCopy = [...invoicesToReturn];
      invoicesToReturnCopy.splice(index, 1);
      setInvoicesToReturn(invoicesToReturnCopy);
    } else {
      // Updated selected rows
      const invoicesToReturnCopy = [...invoicesToReturn];
      invoicesToReturnCopy.splice(index, 1, invoice);
      setInvoicesToReturn(invoicesToReturnCopy);
    }
  };

  const form = useForm<ExtapiInvoiceReturnRequest>();
  const beenInContact = form.watch('beenInContact');

  const nextStepDisabled = useMemo(() => {
    switch (rmaState) {
      case RmaState.GENERAL:
        return !form.formState.isValid;
      case RmaState.INVOICE:
        return invoicesToReturn.length == 0;
      default:
        return true;
    }
  }, [form.formState.isValid, invoicesToReturn.length, rmaState]);

  const onFormSubmit = async (
    returnRequest: ExtapiInvoiceReturnRequest
  ) => {
    const data: ExtapiInvoiceReturnRequest = {
      ...returnRequest,
      returns: invoicesToReturn.flatMap((x) => x.rows),
      beenInContact: returnRequest.beenInContact == 1,
      isDamagedProduct: returnRequest.isDamagedProduct == 1,
    };
    await sendReturnInvoicesRequest(data).then(() =>
      setRmaState(RmaState.FINISH)
    );
  };

  const nextState = () => {
    if (rmaState != RmaState.SUMMARY) {
      setRmaState(rmaState + 1);
    }
  };

  const previousState = () => {
    if (rmaState > 0) {
      setRmaState(rmaState - 1);
    }
  };

  return (
    <div className="col-span-3">
      <h1 className="mb-2">{translate('mypage.rma.title')}</h1>
      <p className="mb-4 max-w-3xl text-lg lg:mb-6">
        {translate('mypage.rma.description')}
      </p>
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => e.preventDefault()}
      >
        {rmaState == RmaState.GENERAL && (
          <>
            <div className="flex flex-col">
              <h5>{translate('mypage.rma.general.title')}</h5>
              <div className="mb-4 flex flex-col gap-2">
                <span className="text-md">
                  {translate('mypage.rma.is.damage.product')}*
                </span>
                <div className="flex gap-2 lg:gap-4">
                  <RadioButton
                    label={translate('mypage.rma.yes')}
                    inputSize="small"
                    wrapperClassName="w-full lg:w-auto"
                    register={form.register}
                    registerOptions={{ required: true }}
                    name="isDamagedProduct"
                    value={1}
                  />
                  <RadioButton
                    label={translate('mypage.rma.no')}
                    inputSize="small"
                    wrapperClassName="w-full lg:w-auto"
                    register={form.register}
                    registerOptions={{ required: true }}
                    name="isDamagedProduct"
                    value={0}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-md">
                  {translate('mypage.rma.been.in.contact')}*
                </span>
                <div className="flex gap-2 lg:gap-4">
                  <div className="checkbox-wrapper checkbox-wrapper--sm w-full lg:w-auto">
                    <label htmlFor="been-in-contact-yes">
                      <input
                        type="radio"
                        className="radio"
                        id="been-in-contact-yes"
                        {...form.register('beenInContact', {
                          required: true,
                        })}
                        value={1}
                      />
                      <span>{translate('mypage.rma.yes')}</span>
                    </label>
                  </div>
                  <div className="checkbox-wrapper checkbox-wrapper--sm w-full lg:w-auto">
                    <label htmlFor="been-in-contact-no">
                      <input
                        type="radio"
                        className="radio"
                        id="been-in-contact-no"
                        {...form.register('beenInContact', {
                          required: true,
                        })}
                        value={0}
                      />
                      <span>{translate('mypage.rma.no')}</span>
                    </label>
                  </div>
                </div>
                {beenInContact == 1 && (
                  <div className="input-wrapper input-wrapper--md mt-4 [&_.input]:text-lg">
                    <label className="!text-md">
                      {translate('mypage.rma.been.in.contact.with.who')}*
                    </label>
                    <input
                      className={clsx('input lg:max-w-[calc(50%_-_8px)]')}
                      type="text"
                      {...form.register('beenInContactPerson')}
                      placeholder={translate('general.placeholder.name')}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <h5>{translate('mypage.rma.reference.title')}</h5>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 md:flex-row lg:gap-4">
                  <div className="input-wrapper input-wrapper--md [&_.input]:text-lg">
                    <label className="!text-md">
                      {translate('mypage.rma.reference.firstname')}*
                    </label>
                    <input
                      className="input"
                      type="text"
                      {...form.register('firstName', { required: true })}
                      placeholder={translate(
                        'general.placeholder.firstname'
                      )}
                    />
                  </div>
                  <div className="input-wrapper input-wrapper--md [&_.input]:text-lg">
                    <label className="!text-md">
                      {translate('mypage.rma.reference.lastname')}*
                    </label>
                    <input
                      className="input"
                      type="text"
                      {...form.register('lastName', { required: true })}
                      placeholder={translate(
                        'general.placeholder.lastname'
                      )}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:flex-row lg:gap-4">
                  <div className="input-wrapper input-wrapper--md [&_.input]:text-lg">
                    <label className="!text-md">
                      {translate('mypage.rma.reference.email')}*
                    </label>
                    <input
                      className="input"
                      type="email"
                      {...form.register('email', { required: true })}
                      placeholder={translate('general.placeholder.email')}
                    />
                  </div>
                  <div className="input-wrapper input-wrapper--md [&_.input]:text-lg">
                    <label className="!text-md">
                      {translate('mypage.rma.reference.phone')}*
                    </label>
                    <input
                      className="input"
                      type="tel"
                      {...form.register('phone', { required: true })}
                      placeholder={translate('general.placeholder.phone')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {rmaState == RmaState.INVOICE && (
          <MyPagesRmaList
            onInvoiceSaved={onInvoiceSaved}
            currentInvoices={invoicesToReturn}
          />
        )}

        {(rmaState == RmaState.SUMMARY || rmaState == RmaState.FINISH) && (
          <div>
            <MyPagesRmaSummary
              form={form.watch()}
              invoices={invoicesToReturn}
            />
          </div>
        )}

        <div className="flex justify-between">
          {rmaState != RmaState.FINISH && (
            <>
              {/* Back button */}
              {rmaState != RmaState.GENERAL && (
                <button
                  className="btn btn--warning btn--medium mr-auto mt-2"
                  onClick={previousState}
                >
                  {translate('mypage.rma.back')}
                </button>
              )}
              {/* Next button */}
              {rmaState != RmaState.SUMMARY && (
                <button
                  className="btn btn--primary btn--medium ml-auto mt-2"
                  disabled={nextStepDisabled}
                  onClick={nextState}
                >
                  {translate('mypage.rma.next')}
                </button>
              )}
            </>
          )}
          {/* Submit button */}
          {rmaState == RmaState.SUMMARY && (
            <button
              className="btn btn--primary btn--medium ml-auto mt-2"
              onClick={form.handleSubmit(onFormSubmit)}
            >
              {translate('mypage.rma.send')}
            </button>
          )}
          {rmaState == RmaState.FINISH && (
            <span className="ml-auto flex items-center gap-1 p-2 font-bold">
              {translate('mypage.rma.sent')}
              <Icon name="check" size="lg"></Icon>
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
